import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { AdminSection } from "./admin";
import { TableShell } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  adminCreateAccessCode,
  adminGrantOwnership,
  adminListCommerce,
  adminRevokeOwnership,
  adminUpdateCatalogCommerce,
} from "@/lib/ownership.functions";
import { formatMoney } from "@/lib/ownership";

export const Route = createFileRoute("/_authenticated/admin/commerce")({
  head: () => ({
    meta: [
      { title: "Commerce · Admin · KAfrequency" },
      { name: "description", content: "Catalog ownership controls, access codes, and entitlement audit trail." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminCommercePage,
});

type CatalogItem = {
  slug: string;
  title: string;
  active: boolean;
  productType: string;
  downloadable: boolean;
  streamable: boolean;
  included_with_membership: boolean;
  bundle_items: string[] | null;
  amount_cents: number | null;
  member_amount_cents: number | null;
  currency: string;
};

function fmt(iso: string | null) {
  return iso ? new Date(iso).toLocaleString() : "—";
}

function AdminCommercePage() {
  const list = useServerFn(adminListCommerce);
  const updateCatalog = useServerFn(adminUpdateCatalogCommerce);
  const grant = useServerFn(adminGrantOwnership);
  const revoke = useServerFn(adminRevokeOwnership);
  const createCode = useServerFn(adminCreateAccessCode);

  const [data, setData] = useState<Awaited<ReturnType<typeof adminListCommerce>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  const [grantForm, setGrantForm] = useState({ userId: "", slug: "", reason: "" });
  const [codeForm, setCodeForm] = useState({ code: "", slugs: "", maxRedemptions: "1", note: "" });

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      setData(await list({}));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load commerce data.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function toggle(slug: string, patch: Record<string, boolean>) {
    setSaving(slug);
    try {
      await updateCatalog({ data: { slug, ...patch } as never });
      toast.success("Catalog updated");
      await refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Update failed");
    } finally {
      setSaving(null);
    }
  }

  if (loading) return <div className="text-muted-foreground">Loading commerce…</div>;
  if (error) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-rose-400">{error}</p>
        <Button variant="outline" onClick={() => void refresh()}>
          Try again
        </Button>
      </div>
    );
  }

  const catalog = (data?.catalog ?? []) as unknown as CatalogItem[];

  return (
    <div className="space-y-10">
      <AdminSection
        title="Catalog ownership"
        description="Control what each product unlocks. Bundles grant every listed slug; membership-included products unlock while a membership is active."
      >
        <TableShell
          headers={["Product", "Type", "Price", "Member price", "Download", "Stream", "In membership", "Live"]}
          rows={catalog.map((row) => (
            <tr key={row.slug}>
              <td className="px-4 py-3">
                <div className="text-foreground">{row.title}</div>
                <div className="font-mono text-xs text-muted-foreground">{row.slug}</div>
                {row.bundle_items?.length ? (
                  <div className="mt-1 text-xs text-gold">Bundle: {row.bundle_items.join(", ")}</div>
                ) : null}
              </td>
              <td className="px-4 py-3 capitalize text-muted-foreground">{row.productType}</td>
              <td className="px-4 py-3">{row.amount_cents ? formatMoney(row.amount_cents, row.currency) : "—"}</td>
              <td className="px-4 py-3">
                {row.member_amount_cents ? formatMoney(row.member_amount_cents, row.currency) : "—"}
              </td>
              <td className="px-4 py-3">
                <Switch
                  checked={row.downloadable}
                  disabled={saving === row.slug}
                  aria-label={`Downloadable: ${row.title}`}
                  onCheckedChange={(v) => void toggle(row.slug, { downloadable: v })}
                />
              </td>
              <td className="px-4 py-3">
                <Switch
                  checked={row.streamable}
                  disabled={saving === row.slug}
                  aria-label={`Streamable: ${row.title}`}
                  onCheckedChange={(v) => void toggle(row.slug, { streamable: v })}
                />
              </td>
              <td className="px-4 py-3">
                <Switch
                  checked={row.included_with_membership}
                  disabled={saving === row.slug}
                  aria-label={`Included with membership: ${row.title}`}
                  onCheckedChange={(v) => void toggle(row.slug, { includedWithMembership: v })}
                />
              </td>
              <td className="px-4 py-3">
                <Switch
                  checked={row.active}
                  disabled={saving === row.slug}
                  aria-label={`Active: ${row.title}`}
                  onCheckedChange={(v) => void toggle(row.slug, { active: v })}
                />
              </td>
            </tr>
          ))}
        />
      </AdminSection>

      <AdminSection
        title="Manual ownership"
        description="Grant or revoke access for support cases. Every action is written to the audit trail below."
      >
        <div className="grid gap-3 sm:grid-cols-4">
          <Input
            placeholder="User ID (uuid)"
            value={grantForm.userId}
            onChange={(e) => setGrantForm({ ...grantForm, userId: e.target.value })}
            aria-label="User ID"
          />
          <Input
            placeholder="product-slug"
            value={grantForm.slug}
            onChange={(e) => setGrantForm({ ...grantForm, slug: e.target.value })}
            aria-label="Product slug"
          />
          <Input
            placeholder="Reason"
            value={grantForm.reason}
            onChange={(e) => setGrantForm({ ...grantForm, reason: e.target.value })}
            aria-label="Reason"
          />
          <div className="flex gap-2">
            <Button
              onClick={async () => {
                try {
                  const res = await grant({ data: grantForm as never });
                  toast.success(`Granted ${res.granted.length} item(s)`);
                  await refresh();
                } catch (e) {
                  toast.error(e instanceof Error ? e.message : "Grant failed");
                }
              }}
            >
              Grant
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  await revoke({ data: grantForm as never });
                  toast.success("Access revoked");
                  await refresh();
                } catch (e) {
                  toast.error(e instanceof Error ? e.message : "Revoke failed");
                }
              }}
            >
              Revoke
            </Button>
          </div>
        </div>
      </AdminSection>

      <AdminSection title="Access codes" description="Gift and partner codes that unlock products without payment.">
        <div className="mb-4 grid gap-3 sm:grid-cols-5">
          <Input
            placeholder="KAF-GIFT-01"
            value={codeForm.code}
            onChange={(e) => setCodeForm({ ...codeForm, code: e.target.value })}
            aria-label="Code"
          />
          <Input
            placeholder="slug-a, slug-b"
            value={codeForm.slugs}
            onChange={(e) => setCodeForm({ ...codeForm, slugs: e.target.value })}
            aria-label="Product slugs"
          />
          <Input
            type="number"
            min={1}
            value={codeForm.maxRedemptions}
            onChange={(e) => setCodeForm({ ...codeForm, maxRedemptions: e.target.value })}
            aria-label="Max redemptions"
          />
          <Input
            placeholder="Note"
            value={codeForm.note}
            onChange={(e) => setCodeForm({ ...codeForm, note: e.target.value })}
            aria-label="Note"
          />
          <Button
            onClick={async () => {
              try {
                await createCode({
                  data: {
                    code: codeForm.code.trim(),
                    productSlugs: codeForm.slugs
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                    maxRedemptions: Number(codeForm.maxRedemptions) || 1,
                    note: codeForm.note || undefined,
                  } as never,
                });
                toast.success("Code created");
                setCodeForm({ code: "", slugs: "", maxRedemptions: "1", note: "" });
                await refresh();
              } catch (e) {
                toast.error(e instanceof Error ? e.message : "Could not create code");
              }
            }}
          >
            Create code
          </Button>
        </div>
        <TableShell
          headers={["Code", "Unlocks", "Redeemed", "Expires", "Active"]}
          rows={(data?.codes ?? []).map((code: any) => (
            <tr key={code.id}>
              <td className="px-4 py-3 font-mono text-xs">{code.code}</td>
              <td className="px-4 py-3">{(code.product_slugs ?? []).join(", ")}</td>
              <td className="px-4 py-3">
                {code.redemption_count} / {code.max_redemptions}
              </td>
              <td className="px-4 py-3 text-muted-foreground">{fmt(code.expires_at)}</td>
              <td className="px-4 py-3">{code.active ? "Yes" : "No"}</td>
            </tr>
          ))}
        />
      </AdminSection>

      <AdminSection title="Entitlement audit trail" description="Every manual grant, revoke, and code unlock.">
        <TableShell
          headers={["When", "Action", "Product", "User", "Reason"]}
          rows={(data?.audit ?? []).map((row: any) => (
            <tr key={row.id}>
              <td className="px-4 py-3 text-muted-foreground">{fmt(row.created_at)}</td>
              <td className="px-4 py-3 capitalize">{row.action}</td>
              <td className="px-4 py-3">{row.product_slug}</td>
              <td className="px-4 py-3 font-mono text-xs">{row.target_user_id?.slice(0, 8) ?? "—"}</td>
              <td className="px-4 py-3 text-muted-foreground">{row.reason ?? "—"}</td>
            </tr>
          ))}
        />
      </AdminSection>
    </div>
  );
}
